import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShieldCheck, CheckCircle2, X, Loader, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

const NAVY = "#0A2540";
const GREEN = "#047857";
const GREEN_DEEP = "#064E3B";

/**
 * Public landing page hit from the Resend "A recruiter wants to chat" email.
 * Token + ?a=1|0 query param decides accept / decline. Server is idempotent
 * so re-clicks just echo the current status.
 *
 * Route: /intro/respond?t=<token>&a=<0|1>
 * Backend: GET /api/intros/respond/{token}?a=<0|1>
 */
export default function IntroRespond() {
  const [params] = useSearchParams();
  const token = params.get("t");
  const a = params.get("a") === "0" ? 0 : 1;
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    let cancel = false;
    if (!token) { setStatus("error"); setMessage("Missing intro token."); return; }
    api.get(`/intros/respond/${token}`, { params: { a } })
      .then(({ data }) => {
        if (cancel) return;
        setStatus(data.status || "done");
        setMessage(data.message || "");
      })
      .catch((e) => {
        if (cancel) return;
        setStatus("error");
        setMessage(e.response?.data?.detail || "This intro link is invalid or expired.");
      });
    return () => { cancel = true; };
  }, [token, a]);

  const headline = status === "accepted"
    ? "Intro accepted."
    : status === "declined"
      ? "Intro declined."
      : status === "loading"
        ? "Working…"
        : "Something went wrong.";

  const tone = status === "accepted"
    ? { bg: "#ECFDF5", border: GREEN, accent: GREEN_DEEP, icon: <CheckCircle2 size={36} style={{ color: GREEN }} /> }
    : status === "declined"
      ? { bg: "#FFF1F2", border: "#9F1239", accent: "#881337", icon: <X size={36} style={{ color: "#9F1239" }} /> }
      : status === "loading"
        ? { bg: "#F8FAFC", border: "#CBD5E1", accent: NAVY, icon: <Loader size={36} className="animate-spin" style={{ color: NAVY }} /> }
        : { bg: "#FEF3C7", border: "#CA8A04", accent: "#78350F", icon: <ShieldCheck size={36} style={{ color: "#CA8A04" }} /> };

  return (
    <div className="min-h-screen rm-paper flex items-center justify-center p-6" data-testid="intro-respond-page">
      <div className="rm-card-static p-8 max-w-lg w-full text-center"
           style={{ background: tone.bg, borderColor: tone.border, boxShadow: `5px 5px 0 0 ${tone.border}` }}>
        <div className="mx-auto mb-3 inline-flex">{tone.icon}</div>
        <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-1"
             style={{ color: tone.accent, fontWeight: 700 }}>
          · Resmo Pro · Talent Cloud
        </div>
        <h1 className="font-display uppercase text-2xl leading-tight mb-2"
            style={{ color: NAVY, letterSpacing: "-0.02em" }}
            data-testid="intro-respond-headline">
          {headline}
        </h1>
        {message && (
          <p className="text-sm leading-snug" style={{ color: "#334155" }} data-testid="intro-respond-message">
            {message}
          </p>
        )}
        <Link to="/"
              className="rm-btn rm-btn-primary mt-4 text-sm px-4 py-2.5 inline-flex"
              data-testid="intro-respond-home">
          Back to my dashboard <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
